<template>
    <div class="manage-bookings-catalog default-content">
        <h1 class="manage-bookings-catalog__title">Manage bookings</h1>

        <div class="container-shadow">
            <div class="manage-bookings-catalog-filters">
                <div class="flex gap-2">
                    <theme-autocomplete
                        class="md:w-full lg:w-[246px] xl:w-[350px]"
                        name="filter_property_id"
                        v-model="searchTerm"
                        :placeholder="locales && locales.properties && locales.properties.property"
                        endpoint='/admin/autocomplete/landlord-properties'
                        :is-phrase-model="true"
                        :resolve="item => item.fullAddress"
                        @input="debouncedFetchData">
                    </theme-autocomplete>
                    
                    <a class="manage-bookings-catalog-filters__calendar-modal-button md:hidden" data-feature="open-modal" data-target="calendar-modal">
                        <span class="icon-calendar icon-orange"></span>
                    </a>
                </div>

                <div v-if="startDate.date || endDate.date" class="manage-bookings-catalog-filters__tags">
                    <div v-if="startDate.date" class="orange-tag">
                        <span class="value">{{ startDate.date }}</span>
                        <span @click="startDate.date = ''" class="icon-close close"></span>
                    </div>
                    <div v-if="endDate.date" class="orange-tag">
                        <span class="value">{{ endDate.date }}</span>
                        <span @click="endDate.date = ''" class="icon-close close"></span>
                    </div>
                </div>

                <div class="manage-bookings-catalog-filters__calendar-buttons">
                    <functional-calendar
                        :configs='calendarConfigs'
                        v-model='startDate'
                        class='single-calendar min-w-[250px] md:w-full'
                        @choseDay='changeStartDate'
                    >
                        <template v-slot:datePickerInput='props'>
                            <theme-input
                                :value="startDateComputed"
                                name='start_date'
                                :isExtraInfo='true'
                                :isReadonly='true'
                                placeholder="Choose start date"
                            >
                                <template slot='extra-info'>
                                    <i class='icon-calendar'/>
                                </template>
                            </theme-input>
                        </template>
                    </functional-calendar>

                    <functional-calendar
                        :configs='calendarConfigs'
                        v-model='endDate'
                        class='single-calendar min-w-[250px] md:w-full'
                        @choseDay='changeEndDate'
                    >
                        <template v-slot:datePickerInput='props'>
                            <theme-input
                                :value="endDateComputed"
                                name='end_date'
                                :isExtraInfo='true'
                                :isReadonly='true'
                                placeholder="Choose end date"
                            >
                                <template slot='extra-info'>
                                    <i class='icon-calendar'/>
                                </template>
                            </theme-input>
                        </template>
                    </functional-calendar>

                    <button v-if="startDate.date || endDate.date" @click="resetDates" class="theme-btn theme-btn--small theme-btn--tertiary whitespace-nowrap">
                        <span class="icon-replay icon-orange"></span>
                        {{ resetDatesText }}
                    </button>
                </div>

            </div>

            <div class="manage-bookings-catalog-list">
                <span class="text-[14px]">{{ (bookings && bookings.data && bookings.data.length) + ' bookings' }}</span>

                <div class="manage-bookings-catalog-list__upper">
                    <div class='theme-order-select'>
                        <select class='theme-order-select__item' name='order' @change="(e) => sort(e, 'orderDirection')">
                            <option v-for="(item, key) in orderDirections" :value="key" :key="key">{{ item }}</option>
                        </select>
                    </div>

                    <div class='theme-order-select'>
                        <select class='theme-order-select__item' name='order' @change="(e) => sort(e, 'orderBy')">
                            <option v-for="(item, key) in ordering" :value="key" :key="key">{{ item }}</option>
                        </select>
                    </div>
                </div>

                <ul class="manage-bookings-catalog-list__list">
                    <li v-for="(booking, i) in bookings.data" :key="booking.id">
                        <ManageBookingsItem :lang="lang" :booking="booking" :token="token" />
                    </li>
                </ul>
            </div>
        </div>

        <!-- Modals -->
        <div class="modal modal--calendar" data-feature="modal" data-name="calendar-modal">
            <div class="modal__content" data-feature="modal-active-area">
                <button class="modal__close" data-feature="close-modal" data-target="calendar-modal">
                    <i class="icon-close"></i>
                </button>
                <h3 class="mb-5">Choose dates</h3>
                <div class="mb-4">
                    <div class="flex gap-4 mb-4">
                        <theme-input
                            :value="startDateComputed"
                            name='start_date'
                            :isExtraInfo='true'
                            :isReadonly='true'
                            placeholder="Choose start date"
                            :class="{'focused': focusedModalInput === 'startDate'}"
                            @focus="() => changeFocusedInput('startDate')"
                        >
                            <template slot='extra-info'>
                                <i class='icon-calendar'/>
                            </template>
                        </theme-input>

                        <theme-input
                            :value="endDateComputed"
                            name='end_date'
                            :isExtraInfo='true'
                            :isReadonly='true'
                            placeholder="Choose end date"
                            :class="{'focused': focusedModalInput === 'endDate'}"
                            @focus="() => changeFocusedInput('endDate')"
                        >
                            <template slot='extra-info'>
                                <i class='icon-calendar'/>
                            </template>
                        </theme-input>
                    </div>

                    <!-- v-model='startDate' -->
                    <functional-calendar
                        :configs='modalCalendarConfigs'
                        class='single-calendar'
                        @choseDay='changeDate'
                    >
                    </functional-calendar>

                    <div class="flex gap-4 justify-between">
                        <button @click="resetDates" class="theme-btn theme-btn--small theme-btn--tertiary pl-0">
                            <span class="icon-replay icon-orange"></span>
                            Reset dates
                        </button>

                        <button data-feature="close-modal" data-target="calendar-modal"
                            class="theme-btn theme-btn--theme"
                            :class="{'theme-btn--theme-disabled': startDate.date === '' && endDate.date === ''}"
                        >
                            Save changes
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { FunctionalCalendar } from 'vue-functional-calendar';
import ManageBookingsItem from './ManageBookingsItem.vue';
import debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';

export default {
    components: { FunctionalCalendar, ManageBookingsItem },
    props: {
        // bookings: {
        //     default: {},
        //     Type: Object,
        // },
        ordering: {
            default: {},
            Type: Object,
        },
        orderDirections: {
            default: {},
            Type: Object,
        },
        token: {
            default: '',
            Type: String,
        },
        resetDatesText: {
            default: '',
            Type: String,
        },
        lang: {
            default: 'nl',
            Type: String,
        },
    },
    data() {
        return {            
            searchTerm: new URL(window.location).searchParams.get('searchTerm') || '',
            startDate: { date: new URL(window.location).searchParams.get('startDate') || '' },
            endDate: { date: new URL(window.location).searchParams.get('endDate') || '' },
            orderBy: new URL(window.location).searchParams.get('orderBy') || '',
            orderDirection: new URL(window.location).searchParams.get('orderDirection') || '',
            calendarConfigs: {
                dateFormat: 'dd-mm-yyyy',
                isModal: true,
                isDatePicker: true,
                isAutoCloseable: true,
                monthNames: this.monthNames,
                dayNames: this.dayNames
            },
            bookings: [],
            modalCalendarConfigs: {
                dateFormat: 'dd-mm-yyyy',
                isDatePicker: true,
                monthNames: this.monthNames,
                dayNames: this.dayNames
            },
            focusedModalInput: 'startDate',
        };
    },
    computed : {
        ...mapGetters(['locales']),
        startDateComputed() {
            return this.startDate.date ? this.startDate.date : '';
        },
        endDateComputed() {
            return this.endDate.date ? this.endDate.date : '';
        },
        filters() {
            return [this.searchTerm, this.startDate, this.endDate, this.orderBy, this.orderDirection]
        }
    },

    methods: {
        fetchData: async function () {
            const query = new URLSearchParams({
                searchTerm: this.searchTerm,
                startDate: this.startDate.date,
                endDate: this.endDate.date,
                orderBy: this.orderBy,
                orderDirection: this.orderDirection,
            }).toString();

            try {
                const response = await fetch(`/api/property-bookings/listing/?${query}`);
                const data = await response.json();
                this.bookings = data;
            } catch (error) {
                console.error('Error fetching data:', error);
                this.bookings = [];
            }
        },
        changeSearchTerm: function (value) {

        },
        changeStartDate: function (value) {
            this.startDate = { date: value.date };
            this.fetchData();
        },
        changeEndDate: function (value) {
            this.endDate = { date: value.date };
            this.fetchData();
        },
        changeFocusedInput: function (inputName) {
            inputName === 'startDate' 
                ? this.focusedModalInput = 'startDate' 
                : this.focusedModalInput = 'endDate'; 
        },
        changeDate: function (value) {
            if (this.focusedModalInput === 'startDate') {
                this.startDate = { date: value.date };
            } else {
                this.endDate = { date: value.date }
            }

            this.fetchData();
        },
        resetDates : function () {
            this.startDate = { date: '' };
            this.endDate = { date: '' };
        },
        sort: function (e, selectType) {
            if (selectType === 'orderBy') {
                this.orderBy = e.target.value;
                this.setSearchParam('orderBy', e.target.value);
            } else if (selectType === 'orderDirection') {
                this.orderDirection = e.target.value;
                this.setSearchParam('orderDirection', e.target.value);
            };
            this.fetchData();
        },
        setSearchParam: function (param, value) {
            const url = new URL(window.location);
            url.searchParams.set(param, value);
            window.history.pushState({}, "", url);
        },
    },
    mounted() {
        // uncomment this when endpoint will be added
        this.fetchData();
    },
    created() {
        this.debouncedFetchData = debounce(this.fetchData, 500);
    },
    watch: {
        filters() {
            this.fetchData();
        }
    }
};
</script>


// WEBPACK FOOTER //
// resources/js/components/ManageBookingsCatalog.vue